import React, { useState, useEffect, useRef } from 'react';
import {
    Bot, BookOpen, Mail, FileText, Receipt, BarChart3,
    Plus, Save, Trash2, Edit3, X, Upload, Image as ImageIcon,
    Check, ChevronRight, ChevronDown, Library, Brain, Sparkles,
    Clock, Zap, Users, FolderOpen, Activity, TrendingUp,
    Play, Pause, Settings, Eye, Calendar, Filter, Search,
    Inbox, CheckCircle, AlertCircle, PauseCircle, FileUp,
    Link2, Unlink, GripVertical, ArrowRight, RefreshCw,
    PieChart, LineChart, Download, Send, MessageSquare,
    Server, Lock, Globe, FolderInput, Timer, MailCheck,
    TestTube, FileSearch, Layers, Target, Crosshair, Ship
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { baseURL } from '../config/APIConfig';


// Avatar options for agent selection
const defaultAvatarOptions = [
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Mark&backgroundColor=b6e3f4',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Sarah&backgroundColor=ffd5dc',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Tom&backgroundColor=c0aede',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Emma&backgroundColor=ffdfbf',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Alex&backgroundColor=d1d4f9',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=John&backgroundColor=c1f4d0',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Lisa&backgroundColor=ffeaa7',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Mike&backgroundColor=dfe6e9',
];

// Agents that require PDF mapping
const PDF_MAPPING_AGENTS = ['booking-agent', 'bl-agent', 'accounting-agent', 'agent-5d7a5039'];

// Default agent configurations
const defaultAgents = [
    {
        id: 'rq-agent',
        name: 'Rate Quote Agent',
        customName: 'Sarah',
        description: 'Handles domestic and international shipping quotes. Reads enquiry emails, searches rate database, applies profit margins.',
        icon: Mail,
        color: '#10b981',
        gradient: 'from-emerald-500 to-teal-500',
        bgGradient: 'from-emerald-500/10 to-teal-500/10',
        systemPrompt: `You are a Rate Quote Agent for a logistics company. Your role is to:
1. Read and understand customer enquiry emails
2. Search the rate database for applicable rates
3. Apply appropriate profit margins (15% standard, 20% hazmat, 10% contract)
4. Create professional rate quotes
5. Follow up with customers on pending quotes

Tone: Professional, helpful, and responsive.
Always verify shipment details before quoting.`,
        capabilities: ['Read enquiry emails', 'Search rate database', 'Create quotes', 'Apply margins', 'Customer follow-up'],
        tools: ['email-parser', 'rate-search', 'pdf-generator'],
        status: 'active',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sarah&backgroundColor=ffd5dc',
        customAvatar: null,
        tasksCompleted: 234,
        successRate: 96.8,
        lastActive: '5 mins ago',
        totalHours: 456,
        requiresPdfMapping: false,
        emailConfig: {
            provider: 'imap',
            imapHost: 'imap.company.com',
            imapPort: 993,
            smtpHost: 'smtp.company.com',
            smtpPort: 587,
            emailAddress: 'quotes@company.com',
            password: '',
            useSSL: true,
            inboxFolder: 'INBOX',
            pollingInterval: 60,
            autoReply: false
        },
        emails: [
            { id: 'e1', subject: 'Rate Quote Request - Shanghai to LA', sender: 'john@acmecorp.com', status: 'completed', date: '2025-03-11', preview: 'Need rates for 2x40HC...' },
            { id: 'e5', subject: 'Urgent: Rate needed for hazmat', sender: 'ops@bigclient.com', status: 'pending', date: '2025-03-11', preview: 'We need urgent rates for...' },
        ],
        dashboardData: {
            tasksByDate: [{ date: 'Mar 5', tasks: 25 }, { date: 'Mar 6', tasks: 32 }, { date: 'Mar 7', tasks: 28 }, { date: 'Mar 8', tasks: 41 }, { date: 'Mar 9', tasks: 35 }, { date: 'Mar 10', tasks: 38 }, { date: 'Mar 11', tasks: 47 }],
            statusBreakdown: { completed: 180, hold: 15, exception: 5 }
        }
    },
    {
        id: 'booking-agent',
        name: 'Booking Agent',
        customName: 'Mark',
        description: 'Creates booking requests, sends to carriers, processes booking confirmations. Includes PDF field mapping for carrier documents.',
        icon: BookOpen,
        color: '#3b82f6',
        gradient: 'from-blue-500 to-cyan-500',
        bgGradient: 'from-blue-500/10 to-cyan-500/10',
        systemPrompt: `You are a Booking Agent responsible for:
1. Creating booking requests in the TMS
2. Sending booking requests to carriers via email/API
3. Monitoring for booking confirmations
4. Extracting data from carrier PDF confirmations
5. Auto-entering booking data into the system

Use the PDF mapping templates to extract fields accurately.
Validate all data before entry.`,
        capabilities: ['Create bookings', 'Send to carriers', 'PDF extraction', 'Data validation', 'Auto-entry'],
        tools: ['pdf-parser', 'ocr-engine', 'carrier-api', 'email-sender'],
        status: 'active',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Mark&backgroundColor=b6e3f4',
        customAvatar: null,
        tasksCompleted: 156,
        successRate: 98.2,
        lastActive: '2 mins ago',
        totalHours: 324,
        requiresPdfMapping: true,
        emailConfig: {
            provider: 'imap',
            imapHost: 'imap.company.com',
            imapPort: 993,
            smtpHost: 'smtp.company.com',
            smtpPort: 587,
            emailAddress: 'booking@company.com',
            password: '',
            useSSL: true,
            inboxFolder: 'INBOX',
            pollingInterval: 60,
            autoReply: false
        },
        emails: [
            { id: 'e2', subject: 'Booking Confirmation #BK123456', sender: 'bookings@maersk.com', status: 'completed', date: '2025-03-11', preview: 'Your booking has been confirmed...' },
        ],
        dashboardData: {
            tasksByDate: [{ date: 'Mar 5', tasks: 15 }, { date: 'Mar 6', tasks: 18 }, { date: 'Mar 7', tasks: 12 }, { date: 'Mar 8', tasks: 22 }, { date: 'Mar 9', tasks: 19 }, { date: 'Mar 10', tasks: 16 }, { date: 'Mar 11', tasks: 24 }],
            statusBreakdown: { completed: 120, hold: 8, exception: 2 }
        },
        pdfMappings: [
            {
                id: 'pm1', carrier: 'Maersk', fields: [
                    { source: 'Booking Number', target: 'carrierBookingNumber', value: 'BK123456' },
                    { source: 'Vessel Name', target: 'vessel', value: 'MAERSK OHIO' },
                    { source: 'Voyage No.', target: 'voyage', value: '607W' },
                    { source: 'Port of Loading', target: 'portOfLoading', value: 'SHANGHAI' },
                    { source: 'Port of Discharge', target: 'portOfDischarge', value: 'LOS ANGELES' },
                    { source: 'ETD', target: 'etd', value: '2025-03-15' },
                    { source: 'ETA', target: 'eta', value: '2025-04-02' },
                    { source: 'Container Type', target: 'containerType', value: '40HC' },
                    { source: 'Container Number', target: 'containerNumber', value: 'MSKU1234567' },
                    { source: 'Shipper', target: 'shipper', value: 'ACME CORP' },
                    { source: 'Consignee', target: 'consignee', value: 'GLOBAL IMPORTS' },
                    { source: 'Weight (KG)', target: 'weight', value: '18500' }
                ], lastUpdated: '2025-03-10', status: 'active', samplePdf: null
            },
            {
                id: 'pm2', carrier: 'MSC', fields: [
                    { source: 'Booking Reference', target: 'carrierBookingNumber', value: '' },
                    { source: 'Mother Vessel', target: 'vessel', value: '' },
                    { source: 'Voyage', target: 'voyage', value: '' },
                    { source: 'Load Port', target: 'portOfLoading', value: '' },
                    { source: 'Discharge Port', target: 'portOfDischarge', value: '' },
                    { source: 'Departure', target: 'etd', value: '' },
                    { source: 'Arrival', target: 'eta', value: '' },
                    { source: 'Equipment', target: 'containerType', value: '' },
                    { source: 'Container No', target: 'containerNumber', value: '' },
                    { source: 'Shipper Name', target: 'shipper', value: '' }
                ], lastUpdated: '2025-03-08', status: 'active', samplePdf: null
            },
            { id: 'pm3', carrier: 'CMA CGM', fields: [], lastUpdated: '2025-03-05', status: 'active', samplePdf: null }
        ]
    },
    {
        id: 'bl-agent',
        name: 'B/L Agent',
        customName: 'Emma',
        description: 'Processes Bills of Lading from emails and attachments. Uses OCR/AI to extract and validate B/L data.',
        icon: FileText,
        color: '#8b5cf6',
        gradient: 'from-violet-500 to-purple-500',
        bgGradient: 'from-violet-500/10 to-purple-500/10',
        systemPrompt: `You are a B/L (Bill of Lading) Agent. Your responsibilities:
1. Monitor the B/L email inbox
2. Extract B/L documents from email attachments
3. Use OCR to read scanned documents
4. Extract all B/L fields accurately
5. Validate against booking data
6. Enter verified data into the TMS

Flag any discrepancies for human review.`,
        capabilities: ['Email monitoring', 'OCR extraction', 'Data validation', 'TMS entry', 'Discrepancy flagging'],
        tools: ['ocr-engine', 'email-parser', 'validation-api'],
        status: 'active',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Emma&backgroundColor=ffdfbf',
        customAvatar: null,
        tasksCompleted: 312,
        successRate: 97.5,
        lastActive: '30 secs ago',
        totalHours: 512,
        requiresPdfMapping: true,
        emailConfig: {
            provider: 'imap',
            imapHost: 'imap.company.com',
            imapPort: 993,
            smtpHost: 'smtp.company.com',
            smtpPort: 587,
            emailAddress: 'bl@company.com',
            password: '',
            useSSL: true,
            inboxFolder: 'INBOX',
            pollingInterval: 60,
            autoReply: false
        },
        emails: [
            { id: 'e3', subject: 'B/L Draft - MSKU1234567', sender: 'docs@msc.com', status: 'hold', date: '2025-03-10', preview: 'Please find attached draft B/L...' },
        ],
        dashboardData: {
            tasksByDate: [{ date: 'Mar 5', tasks: 35 }, { date: 'Mar 6', tasks: 42 }, { date: 'Mar 7', tasks: 38 }, { date: 'Mar 8', tasks: 51 }, { date: 'Mar 9', tasks: 45 }, { date: 'Mar 10', tasks: 48 }, { date: 'Mar 11', tasks: 53 }],
            statusBreakdown: { completed: 245, hold: 18, exception: 8 }
        },
        pdfMappings: [
            {
                id: 'pm4', carrier: 'Universal B/L', fields: [
                    { source: 'B/L Number', target: 'blNumber', value: '' },
                    { source: 'Shipper', target: 'shipper', value: '' },
                    { source: 'Consignee', target: 'consignee', value: '' },
                    { source: 'Notify Party', target: 'notifyParty', value: '' },
                    { source: 'Vessel', target: 'vessel', value: '' },
                    { source: 'Port of Loading', target: 'portOfLoading', value: '' },
                    { source: 'Port of Discharge', target: 'portOfDischarge', value: '' },
                    { source: 'Description of Goods', target: 'description', value: '' },
                    { source: 'Gross Weight', target: 'grossWeight', value: '' },
                    { source: 'Measurement', target: 'measurement', value: '' }
                ], lastUpdated: '2025-03-10', status: 'active', samplePdf: null
            }
        ]
    },
    {
        id: 'tracking-agent',
        name: 'Tracking Agent',
        customName: 'Alex',
        description: 'Monitors shipment status, provides tracking updates, and alerts on delays or exceptions.',
        icon: Activity,
        color: '#ec4899',
        gradient: 'from-pink-500 to-rose-500',
        bgGradient: 'from-pink-500/10 to-rose-500/10',
        systemPrompt: `You are a Tracking Agent responsible for:
1. Monitoring all active shipments
2. Fetching status updates from carrier APIs
3. Updating TMS with latest positions
4. Alerting customers on significant events
5. Flagging delayed shipments

Proactively notify stakeholders of any issues.`,
        capabilities: ['Status monitoring', 'Carrier API integration', 'Customer alerts', 'Delay detection', 'Exception handling'],
        tools: ['tracking-api', 'notification-engine', 'email-sender'],
        status: 'active',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Alex&backgroundColor=d1d4f9',
        customAvatar: null,
        tasksCompleted: 178,
        successRate: 95.3,
        lastActive: '15 mins ago',
        totalHours: 256,
        requiresPdfMapping: false,
        emailConfig: {
            provider: 'imap',
            imapHost: 'imap.company.com',
            imapPort: 993,
            smtpHost: 'smtp.company.com',
            smtpPort: 587,
            emailAddress: 'tracking@company.com',
            password: '',
            useSSL: true,
            inboxFolder: 'INBOX',
            pollingInterval: 30,
            autoReply: false
        },
        emails: [],
        dashboardData: {
            tasksByDate: [{ date: 'Mar 5', tasks: 20 }, { date: 'Mar 6', tasks: 25 }, { date: 'Mar 7', tasks: 18 }, { date: 'Mar 8', tasks: 30 }, { date: 'Mar 9', tasks: 28 }, { date: 'Mar 10', tasks: 22 }, { date: 'Mar 11', tasks: 35 }],
            statusBreakdown: { completed: 140, hold: 10, exception: 8 }
        }
    },
    {
        id: 'accounting-agent',
        name: 'Accounting Agent',
        customName: 'Tom',
        description: 'Audits vendor invoices, creates dispute lists, generates vendor bills, and reconciles accounts.',
        icon: Receipt,
        color: '#f59e0b',
        gradient: 'from-amber-500 to-orange-500',
        bgGradient: 'from-amber-500/10 to-orange-500/10',
        systemPrompt: `You are an Accounting Agent for invoice processing:
1. Receive and parse vendor invoices
2. Compare charges against TMS records
3. Flag discrepancies over $50 or 5%
4. Create dispute lists for review
5. Generate approved vendor bills

Maintain audit trail for all actions.`,
        capabilities: ['Invoice parsing', 'Charge comparison', 'Dispute creation', 'Bill generation', 'Audit logging'],
        tools: ['pdf-parser', 'accounting-api', 'email-parser'],
        status: 'inactive',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Tom&backgroundColor=c0aede',
        customAvatar: null,
        tasksCompleted: 89,
        successRate: 99.1,
        lastActive: '1 hour ago',
        totalHours: 128,
        requiresPdfMapping: true,
        emailConfig: {
            provider: 'imap',
            imapHost: 'imap.company.com',
            imapPort: 993,
            smtpHost: 'smtp.company.com',
            smtpPort: 587,
            emailAddress: 'accounting@company.com',
            password: '',
            useSSL: true,
            inboxFolder: 'INBOX',
            pollingInterval: 120,
            autoReply: false
        },
        emails: [
            { id: 'e4', subject: 'Invoice #INV-2025-0892', sender: 'accounts@carrier.com', status: 'exception', date: '2025-03-10', preview: 'Attached invoice for services...' },
        ],
        dashboardData: {
            tasksByDate: [{ date: 'Mar 5', tasks: 8 }, { date: 'Mar 6', tasks: 12 }, { date: 'Mar 7', tasks: 6 }, { date: 'Mar 8', tasks: 15 }, { date: 'Mar 9', tasks: 10 }, { date: 'Mar 10', tasks: 8 }, { date: 'Mar 11', tasks: 14 }],
            statusBreakdown: { completed: 65, hold: 5, exception: 9 }
        },
        pdfMappings: [
            {
                id: 'pm5', carrier: 'Invoice Template', fields: [
                    { source: 'Invoice Number', target: 'invoiceNumber', value: '' },
                    { source: 'Invoice Date', target: 'invoiceDate', value: '' },
                    { source: 'Vendor Name', target: 'vendorName', value: '' },
                    { source: 'Amount', target: 'amount', value: '' },
                    { source: 'Reference', target: 'reference', value: '' },
                    { source: 'Due Date', target: 'dueDate', value: '' }
                ], lastUpdated: '2025-03-01', status: 'draft', samplePdf: null
            }
        ]
    },
    {
        id: 'agent-5d7a5039',
        name: 'Ocean Import B/L Agent',
        customName: 'Alok',
        description: 'AI-powered B/L agent for Ocean Import. Reads and processes arrival notices, extracts shipment details, and auto-generates delivery/release orders.',
        icon: Ship,
        color: '#0ea5e9',
        gradient: 'from-sky-500 to-blue-600',
        bgGradient: 'from-sky-500/10 to-blue-600/10',
        systemPrompt: `You are Alok, an expert Ocean Import B/L Agent specializing in arrival notice processing. Your responsibilities:

1. ARRIVAL NOTICE PROCESSING:
- Read and parse arrival notice emails and PDF attachments from carriers (COSCO, MSC, Maersk, Hapag-Lloyd, ONE, CMA CGM, Evergreen, Yang Ming)
- Extract key fields: Vessel Name, Voyage Number, ETA, ETD, Port of Loading (POL), Port of Discharge (POD), Final Destination
- Extract B/L details: Master B/L Number, House B/L Number, Sub B/L Number, AMS B/L Number
- Extract party information: Consignee name and address, Notify Party, Shipper/Exporter, Customs Broker, Trucker
- Extract container details: Container Number, Seal Number, Container Type, Weight (KGS/LBS), Measurement (CBM/CFT), Packages, Cargo Description, HS Code
- Extract dates: Cargo Cut-off, VGM Cut-off, SI Cut-off, Last Free Day, Customs Release Date, Carrier Release Date

2. DOCUMENT GENERATION:
- Auto-generate Delivery Orders from arrival notice data
- Auto-generate Release Orders (Telex/Express/Original) from arrival notice data
- Pre-fill import booking forms with extracted arrival notice data
- Generate HBL drafts from arrival notice information

3. COMPLIANCE & VALIDATION:
- Validate AMS/ISF filing requirements
- Check for missing or inconsistent data fields
- Flag containers with hazmat indicators
- Verify port codes and carrier SCAC codes

4. NOTIFICATION & TRACKING:
- Alert file managers when arrival notices are received
- Track ETA changes and notify relevant parties
- Monitor customs clearance status

Always extract data accurately. When fields are ambiguous, flag them for manual review.`,
        capabilities: ['Arrival notice parsing', 'PDF extraction', 'Delivery order generation', 'Release order generation', 'AMS/ISF validation', 'ETA tracking'],
        tools: ['email-parser', 'pdf-parser', 'ocr-engine', 'validation-api', 'carrier-api', 'notification-engine'],
        status: 'active',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Alok&backgroundColor=b6e3f4',
        customAvatar: null,
        tasksCompleted: 0,
        successRate: 0,
        lastActive: 'New',
        totalHours: 0,
        requiresPdfMapping: true,
        emailConfig: {
            provider: 'imap',
            imapHost: 'imap.ionos.com',
            imapPort: 993,
            smtpHost: 'smtp.ionos.com',
            smtpPort: 587,
            emailAddress: 'importbl@infoxwebware.net',
            password: '',
            useSSL: true,
            inboxFolder: 'INBOX',
            pollingInterval: 60,
            autoReply: false
        },
        emails: [],
        dashboardData: {
            tasksByDate: [],
            statusBreakdown: { completed: 0, hold: 0, exception: 0 }
        },
        pdfMappings: [
            {
                id: 'pm6', carrier: 'Arrival Notice Template', fields: [
                    { source: 'Master B/L Number', target: 'masterBLNumber', value: '' },
                    { source: 'House B/L Number', target: 'houseBLNumber', value: '' },
                    { source: 'Vessel', target: 'vessel', value: '' },
                    { source: 'Voyage', target: 'voyage', value: '' },
                    { source: 'Port of Loading', target: 'portOfLoading', value: '' },
                    { source: 'Port of Discharge', target: 'portOfDischarge', value: '' },
                    { source: 'ETA', target: 'eta', value: '' },
                    { source: 'ETD', target: 'etd', value: '' },
                    { source: 'Consignee', target: 'consignee', value: '' },
                    { source: 'Notify Party', target: 'notifyParty', value: '' },
                    { source: 'Container Number', target: 'containerNumber', value: '' },
                    { source: 'Container Type', target: 'containerType', value: '' },
                    { source: 'Gross Weight', target: 'grossWeight', value: '' },
                    { source: 'Cargo Description', target: 'description', value: '' },
                    { source: 'Last Free Day', target: 'lastFreeDay', value: '' }
                ], lastUpdated: '2026-04-15', status: 'active', samplePdf: null
            }
        ]
    }
];

// Available tools for agents
const availableTools = [
    { id: 'pdf-parser', name: 'PDF Parser', description: 'Extract text and data from PDF documents' },
    { id: 'ocr-engine', name: 'OCR Engine', description: 'Optical character recognition for scanned documents' },
    { id: 'email-parser', name: 'Email Parser', description: 'Parse and extract data from emails' },
    { id: 'email-sender', name: 'Email Sender', description: 'Send emails and notifications' },
    { id: 'rate-search', name: 'Rate Search', description: 'Search rate database for pricing' },
    { id: 'carrier-api', name: 'Carrier API', description: 'Connect to carrier booking systems' },
    { id: 'tracking-api', name: 'Tracking API', description: 'Fetch shipment tracking updates' },
    { id: 'accounting-api', name: 'Accounting API', description: 'Integration with accounting systems' },
    { id: 'pdf-generator', name: 'PDF Generator', description: 'Generate PDF documents and quotes' },
    { id: 'validation-api', name: 'Validation API', description: 'Validate data against business rules' },
    { id: 'notification-engine', name: 'Notification Engine', description: 'Send alerts and notifications' }
];

// API field options for PDF mapping
const apiFieldOptions = [
    'carrierBookingNumber', 'vessel', 'voyage', 'portOfLoading', 'portOfDischarge',
    'etd', 'eta', 'containerType', 'containerNumber', 'shipper', 'consignee',
    'notifyParty', 'weight', 'measurement', 'description', 'blNumber',
    'invoiceNumber', 'invoiceDate', 'vendorName', 'amount', 'reference', 'dueDate'
];

const AIAgentSetup = () => {
    const [agents, setAgents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('agents');
    const [showAgentModal, setShowAgentModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showMappingModal, setShowMappingModal] = useState(false);
    const [showMappingEditor, setShowMappingEditor] = useState(false);
    const [editingAgent, setEditingAgent] = useState(null);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [dateRange, setDateRange] = useState('7d');
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [editingMapping, setEditingMapping] = useState(null);
    const [testResults, setTestResults] = useState(null);
    const [isTestingMapping, setIsTestingMapping] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const fileInputRef = useRef(null);
    const pdfInputRef = useRef(null);
    const carrierLogoInputRef = useRef(null);

    // const API_URL = process.env.REACT_APP_BACKEND_URL;
    // Use pyproxy for AI agents from pyapi.shipez.ai
    const API_URL = `${baseURL.dashboardApi}`;

    // Agent form state
    const [agentForm, setAgentForm] = useState({
        customName: '',
        description: '',
        systemPrompt: '',
        avatar: defaultAvatarOptions[0],
        customAvatar: null,
        tools: [],
        status: 'active',
        emailConfig: {
            provider: 'imap',
            imapHost: '',
            imapPort: 993,
            smtpHost: '',
            smtpPort: 587,
            emailAddress: '',
            password: '',
            useSSL: true,
            inboxFolder: 'INBOX',
            pollingInterval: 60,
            autoReply: false
        }
    });

    // Fetch agents from API on mount
    useEffect(() => {
        fetchAgents();
    }, []);

    const fetchAgents = async () => {
        try {
            setIsLoading(true);
            console.log('Fetching agents from:', `${API_URL}/ai-agents`);
            const response = await axios.get(`${API_URL}/ai-agents`, { timeout: 10000 });
            console.log('Agents response:', response.data?.success, response.data?.agents?.length);
            if (response.data.success && response.data.agents?.length > 0) {
                // Merge API data with default UI properties (icons, etc.)
                const apiAgents = response.data.agents.map(apiAgent => {
                    const defaultAgent = defaultAgents.find(d => d.id === apiAgent.id);
                    return {
                        ...defaultAgent,
                        ...apiAgent,
                        icon: defaultAgent?.icon || Bot,
                        bgGradient: defaultAgent?.bgGradient || 'from-gray-500/10 to-slate-500/10',
                        // Also fetch mappings for this agent
                        pdfMappings: []
                    };
                });
                console.log('total api agent data:', apiAgents);
                setAgents(apiAgents);
                setIsLoading(false);

                // Fetch mappings for each agent that requires PDF mapping
                for (const agent of apiAgents) {
                    if (PDF_MAPPING_AGENTS.includes(agent.id)) {
                        fetchAgentMappings(agent.id);
                    }
                }
            } else {
                // Fallback to defaults if API returns no agents
                console.log('API returned empty or success=false, using defaults');
                setAgents(defaultAgents);
                setIsLoading(false);
            }
        } catch (error) {
            console.error('Failed to fetch agents:', error);
            // Fall back to default agents if API fails
            setAgents(defaultAgents);
            setIsLoading(false);
            toast.error('Using default configuration (API unavailable)');
        }
    };

    const fetchAgentMappings = async (agentId) => {
        try {
            const response = await axios.get(`${API_URL}/ai-agents/${agentId}/mappings`);
            if (response.data.success) {
                setAgents(prev => prev.map(agent =>
                    agent.id === agentId
                        ? { ...agent, pdfMappings: response.data.mappings }
                        : agent
                ));
            }
        } catch (error) {
            console.error(`Failed to fetch mappings for ${agentId}:`, error);
        }
    };

    // Set first agent as selected by default
    useEffect(() => {
        if (agents.length > 0 && !selectedAgent) {
            setSelectedAgent(agents[0]);
        }
    }, [agents, selectedAgent]);

    const inputClass = "w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-800 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-lime-500/20 focus:border-lime-500 transition-all";
    const selectClass = `${inputClass} appearance-none cursor-pointer`;

    // Profile image upload handler
    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!validTypes.includes(file.type)) {
            toast.error('Invalid file type. Please upload JPG or PNG image.');
            return;
        }

        // Validate file size (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            toast.error('File too large. Maximum size is 2MB.');
            return;
        }

        // Create preview URL
        const reader = new FileReader();
        reader.onloadend = () => {
            setAgentForm(prev => ({
                ...prev,
                customAvatar: reader.result,
                avatar: reader.result
            }));
            toast.success('Profile image uploaded successfully');
        };
        reader.readAsDataURL(file);
    };

    // PDF upload handler for mapping
    const handlePdfUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.type !== 'application/pdf') {
            toast.error('Please upload a PDF file.');
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            toast.error('File too large. Maximum size is 10MB.');
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            if (editingMapping) {
                setEditingMapping(prev => ({
                    ...prev,
                    samplePdf: reader.result,
                    pdfFileName: file.name
                }));
                // Simulate OCR field detection and auto-mapping
                simulateOcrDetection(file.name);
            }
            toast.success('PDF uploaded successfully. Analyzing document...');
        };
        reader.readAsDataURL(file);
    };

    // Carrier logo upload handler for mapping
    const handleCarrierLogoUpload = (e, mappingId) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            toast.error('Invalid file type. Please upload JPG, PNG, SVG, or WebP image.');
            return;
        }

        // Validate file size (max 1MB for logos)
        if (file.size > 1 * 1024 * 1024) {
            toast.error('File too large. Maximum size is 1MB.');
            return;
        }

        const reader = new FileReader();
        reader.onloadend = async () => {
            const logoData = reader.result;

            // Update the mapping in the agent's pdfMappings
            if (selectedAgent) {
                const updatedMappings = (selectedAgent.pdfMappings || []).map(m =>
                    m.id === mappingId ? { ...m, carrierLogo: logoData } : m
                );

                // Update local state
                setAgents(prev => prev.map(a =>
                    a.id === selectedAgent.id ? { ...a, pdfMappings: updatedMappings } : a
                ));

                // Save to backend
                try {
                    const mapping = updatedMappings.find(m => m.id === mappingId);
                    if (mapping) {
                        await axios.put(`${API_URL}/ai-agents/${selectedAgent.id}/mappings/${mappingId}`, {
                            ...mapping,
                            carrierLogo: logoData
                        });
                    }
                    toast.success('Carrier logo uploaded successfully');
                } catch (error) {
                    console.error('Failed to save carrier logo:', error);
                    toast.error('Failed to save carrier logo');
                }
            }
        };
        reader.readAsDataURL(file);
    };

    // Simulate OCR detection and auto-map fields
    const simulateOcrDetection = (fileName) => {
        // Simulated extracted fields from PDF based on common booking document fields
        // In production, this would call an actual OCR/AI API
        const detectedFields = [
            { source: 'Booking Number', target: 'carrierBookingNumber', value: 'BK' + Math.random().toString(36).substring(2, 8).toUpperCase(), confidence: 95 },
            { source: 'Vessel Name', target: 'vessel', value: 'MAERSK OHIO', confidence: 92 },
            { source: 'Voyage No.', target: 'voyage', value: '607W', confidence: 94 },
            { source: 'Port of Loading', target: 'portOfLoading', value: 'SHANGHAI', confidence: 98 },
            { source: 'Port of Discharge', target: 'portOfDischarge', value: 'LOS ANGELES', confidence: 97 },
            { source: 'ETD', target: 'etd', value: '2025-03-15', confidence: 89 },
            { source: 'ETA', target: 'eta', value: '2025-04-02', confidence: 88 },
            { source: 'Container Type', target: 'containerType', value: '40HC', confidence: 96 },
            { source: 'Container Number', target: 'containerNumber', value: 'MSKU' + Math.random().toString(36).substring(2, 9).toUpperCase(), confidence: 91 },
            { source: 'Shipper', target: 'shipper', value: 'ACME CORPORATION', confidence: 93 },
            { source: 'Consignee', target: 'consignee', value: 'GLOBAL IMPORTS LLC', confidence: 90 },
            { source: 'Weight (KG)', target: 'weight', value: '18500', confidence: 87 },
        ];

        setTimeout(() => {
            // Auto-populate the mapping fields
            setEditingMapping(prev => ({
                ...prev,
                fields: detectedFields.map(f => ({
                    source: f.source,
                    target: f.target,
                    value: f.value
                }))
            }));

            // Set test results to show extraction confidence
            setTestResults({
                success: true,
                extractedFields: detectedFields.map(f => ({
                    field: f.source,
                    value: f.value,
                    confidence: f.confidence
                })),
                processingTime: '1.8s'
            });

            toast.success(`Document analyzed! ${detectedFields.length} fields auto-mapped. Review and adjust as needed.`);
        }, 2000);
    };

    const handleEditAgent = (agent) => {
        setEditingAgent(agent);
        setAgentForm({
            customName: agent.customName,
            description: agent.description,
            systemPrompt: agent.systemPrompt || '',
            avatar: agent.customAvatar || agent.avatar,
            customAvatar: agent.customAvatar,
            tools: agent.tools || [],
            status: agent.status,
            emailConfig: agent.emailConfig || {
                provider: 'imap',
                imapHost: '',
                imapPort: 993,
                smtpHost: '',
                smtpPort: 587,
                emailAddress: '',
                password: '',
                useSSL: true,
                inboxFolder: 'INBOX',
                pollingInterval: 60,
                autoReply: false
            }
        });
        setShowAgentModal(true);
    };

    const handleCreateAgent = () => {
        setEditingAgent(null);
        setAgentForm({
            customName: '',
            description: '',
            systemPrompt: '',
            avatar: defaultAvatarOptions[0],
            customAvatar: null,
            tools: [],
            status: 'inactive',
            emailConfig: {
                provider: 'imap',
                imapHost: '',
                imapPort: 993,
                smtpHost: '',
                smtpPort: 587,
                emailAddress: '',
                password: '',
                useSSL: true,
                inboxFolder: 'INBOX',
                pollingInterval: 60,
                autoReply: false
            }
        });
        setShowCreateModal(true);
    };

    const handleSaveAgent = async () => {
        if (!agentForm.customName.trim()) {
            toast.error('Please enter agent name');
            return;
        }

        setIsSaving(true);
        try {
            if (editingAgent) {
                // Update existing agent via API
                const response = await axios.put(`${API_URL}/ai-agents/${editingAgent.id}`, {
                    customName: agentForm.customName,
                    description: agentForm.description,
                    systemPrompt: agentForm.systemPrompt,
                    avatar: agentForm.avatar,
                    customAvatar: agentForm.customAvatar,
                    status: agentForm.status,
                    emailConfig: agentForm.emailConfig,
                    capabilities: agentForm.tools
                });

                if (response.data.success) {
                    setAgents(prev => prev.map(agent =>
                        agent.id === editingAgent.id
                            ? { ...agent, ...response.data.agent, icon: agent.icon, bgGradient: agent.bgGradient }
                            : agent
                    ));
                    toast.success(`Agent "${agentForm.customName}" saved successfully`);
                }
            } else {
                // Create new agent via API
                const response = await axios.post(`${API_URL}/ai-agents`, {
                    name: 'Custom Agent',
                    customName: agentForm.customName,
                    description: agentForm.description,
                    systemPrompt: agentForm.systemPrompt,
                    avatar: agentForm.avatar,
                    customAvatar: agentForm.customAvatar,
                    status: agentForm.status,
                    emailConfig: agentForm.emailConfig,
                    capabilities: agentForm.tools
                });

                if (response.data.success) {
                    const newAgent = {
                        ...response.data.agent,
                        icon: Bot,
                        bgGradient: 'from-indigo-500/10 to-purple-500/10',
                        pdfMappings: []
                    };
                    setAgents(prev => [...prev, newAgent]);
                    setSelectedAgent(newAgent);
                    toast.success(`Agent "${agentForm.customName}" created successfully`);
                }
            }
            setShowAgentModal(false);
            setShowCreateModal(false);
        } catch (error) {
            console.error('Failed to save agent:', error);
            toast.error('Failed to save agent. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteAgent = async (agentId) => {
        const agent = agents.find(a => a.id === agentId);
        try {
            await axios.delete(`${API_URL}/ai-agents/${agentId}`);
            setAgents(prev => prev.filter(a => a.id !== agentId));
            if (selectedAgent?.id === agentId) {
                setSelectedAgent(agents.find(a => a.id !== agentId) || null);
            }
            toast.success(`Agent "${agent.customName}" deleted`);
        } catch (error) {
            console.error('Failed to delete agent:', error);
            toast.error('Failed to delete agent');
        }
    };

    const handleStatusToggle = async (agentId) => {
        const agent = agents.find(a => a.id === agentId);
        const newStatus = agent.status === 'active' ? 'inactive' : 'active';

        try {
            await axios.put(`${API_URL}/ai-agents/${agentId}`, { status: newStatus });
            setAgents(prev => prev.map(a =>
                a.id === agentId ? { ...a, status: newStatus } : a
            ));
        } catch (error) {
            console.error('Failed to toggle status:', error);
            toast.error('Failed to update status');
        }
    };

    const handleEmailStatus = (emailId, newStatus) => {
        if (!selectedAgent) return;
        setAgents(prev => prev.map(agent =>
            agent.id === selectedAgent.id
                ? {
                    ...agent,
                    emails: agent.emails.map(email =>
                        email.id === emailId ? { ...email, status: newStatus } : email
                    )
                }
                : agent
        ));
        toast.success(`Email marked as ${newStatus}`);
    };

    const toggleTool = (toolId) => {
        setAgentForm(prev => ({
            ...prev,
            tools: prev.tools.includes(toolId)
                ? prev.tools.filter(t => t !== toolId)
                : [...prev.tools, toolId]
        }));
    };

    const handleEmailConfigChange = (field, value) => {
        setAgentForm(prev => ({
            ...prev,
            emailConfig: { ...prev.emailConfig, [field]: value }
        }));
    };

    // PDF Mapping handlers
    const handleEditMapping = (mapping) => {
        setEditingMapping({ ...mapping });
        setShowMappingEditor(true);
    };

    const handlePreviewMapping = (mapping) => {
        setEditingMapping({ ...mapping });
        setTestResults({
            success: true,
            extractedFields: mapping.fields.filter(f => f.value).map(f => ({
                field: f.target,
                value: f.value,
                confidence: Math.floor(Math.random() * 15) + 85
            }))
        });
        setShowMappingEditor(true);
    };

    const handleTestMapping = async () => {
        if (!editingMapping) return;

        setIsTestingMapping(true);
        // Simulate API call for testing
        await new Promise(resolve => setTimeout(resolve, 2000));

        const results = {
            success: true,
            extractedFields: editingMapping.fields.map(f => ({
                field: f.target,
                value: f.value || `Sample ${f.source}`,
                confidence: Math.floor(Math.random() * 20) + 80
            })),
            errors: [],
            processingTime: '1.2s'
        };

        setTestResults(results);
        setIsTestingMapping(false);
        toast.success('Test extraction completed successfully');
    };

    const handleSaveMapping = async () => {
        if (!selectedAgent || !editingMapping) return;

        if (!editingMapping.carrier?.trim()) {
            toast.error('Please enter carrier name');
            return;
        }

        setIsSaving(true);
        try {
            const mappingData = {
                id: editingMapping.id,
                carrier: editingMapping.carrier,
                templateName: editingMapping.templateName || 'Document Template',
                samplePdf: editingMapping.samplePdf,
                pdfFileName: editingMapping.pdfFileName,
                fields: editingMapping.fields || []
            };

            // Check if this is a new mapping or existing one
            const isNewMapping = editingMapping.id?.startsWith('pm-') || !selectedAgent.pdfMappings?.find(m => m.id === editingMapping.id);

            let response;
            if (isNewMapping) {
                response = await axios.post(`${API_URL}/ai-agents/${selectedAgent.id}/mappings`, mappingData);
            } else {
                response = await axios.put(`${API_URL}/ai-agents/${selectedAgent.id}/mappings/${editingMapping.id}`, mappingData);
            }

            if (response.data.success) {
                // Refresh mappings from API
                await fetchAgentMappings(selectedAgent.id);
                toast.success('Mapping configuration saved successfully');
            }
        } catch (error) {
            console.error('Failed to save mapping:', error);
            toast.error('Failed to save mapping. Please try again.');
        } finally {
            setIsSaving(false);
            setShowMappingEditor(false);
            setEditingMapping(null);
            setTestResults(null);
        }
    };

    const handleAddMapping = () => {
        const newMapping = {
            id: `pm-${Date.now()}`,
            carrier: '',
            templateName: 'Document Template',
            fields: [],
            lastUpdated: new Date().toISOString().split('T')[0],
            status: 'draft',
            samplePdf: null,
            pdfFileName: null
        };
        setEditingMapping(newMapping);
        setShowMappingEditor(true);
    };

    const handleDeleteMapping = async (mappingId) => {
        if (!selectedAgent) return;

        try {
            await axios.delete(`${API_URL}/ai-agents/${selectedAgent.id}/mappings/${mappingId}`);
            await fetchAgentMappings(selectedAgent.id);
            toast.success('Mapping deleted successfully');
        } catch (error) {
            console.error('Failed to delete mapping:', error);
            toast.error('Failed to delete mapping');
        }
    };

    const handleAddField = () => {
        if (!editingMapping) return;
        setEditingMapping(prev => ({
            ...prev,
            fields: [...prev.fields, { source: '', target: '', value: '' }]
        }));
    };

    const handleRemoveField = (index) => {
        if (!editingMapping) return;
        setEditingMapping(prev => ({
            ...prev,
            fields: prev.fields.filter((_, i) => i !== index)
        }));
    };

    const handleFieldChange = (index, field, value) => {
        if (!editingMapping) return;
        setEditingMapping(prev => ({
            ...prev,
            fields: prev.fields.map((f, i) => i === index ? { ...f, [field]: value } : f)
        }));
    };

    // Filter emails by selected agent
    const filteredEmails = selectedAgent?.emails?.filter(email => {
        const matchesSearch = email.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
            email.sender.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus = statusFilter === 'all' || email.status === statusFilter;
        return matchesSearch && matchesStatus;
    }) || [];

    const totalTasks = Array.isArray(agents)
        ? agents.reduce((acc, a) => acc + Number(a.tasksCompleted || 0), 0)
        : 0;
    const avgSuccessRate = agents.length > 0
        ? (agents.reduce((acc, a) => acc + Number(a.successRate || 0), 0) / agents.length).toFixed(1)
        : 0;

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed': return <CheckCircle className="w-4 h-4 text-emerald-500" />;
            case 'hold': return <PauseCircle className="w-4 h-4 text-amber-500" />;
            case 'exception': return <AlertCircle className="w-4 h-4 text-red-500" />;
            default: return <Clock className="w-4 h-4 text-blue-500" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400';
            case 'hold': return 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400';
            case 'exception': return 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400';
            default: return 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400';
        }
    };

    // Full page loading state for initial load - minimal skeleton
    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-cyan-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
                <div className="p-6 lg:p-8">
                    {/* Header Skeleton */}
                    <div className="mb-8 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="p-3 rounded-2xl bg-gradient-to-br from-lime-500 to-emerald-500 shadow-lg shadow-lime-500/20">
                                <Bot className="w-8 h-8 text-white" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-slate-800 dark:text-white">AI Agent Setup</h1>
                                <p className="text-slate-500 dark:text-gray-400 mt-1">Loading agents...</p>
                            </div>
                        </div>
                    </div>

                    {/* Agent Cards Skeleton */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3, 4, 5].map((i) => (
                            <div key={i} className="rounded-2xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-white/5 p-6 animate-pulse">
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="w-14 h-14 rounded-xl bg-slate-200 dark:bg-slate-700" />
                                    <div className="flex-1">
                                        <div className="h-5 w-24 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
                                        <div className="h-4 w-32 bg-slate-100 dark:bg-slate-800 rounded" />
                                    </div>
                                </div>
                                <div className="h-4 w-full bg-slate-100 dark:bg-slate-800 rounded mb-2" />
                                <div className="h-4 w-3/4 bg-slate-100 dark:bg-slate-800 rounded" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-cyan-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
            {/* Background Effects */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none dark:block hidden">
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
                <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />
            </div>

            <div className="relative z-10 p-6 lg:p-8">
                {/* Header */}
                <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-4">
                            <div className="relative">
                                <div className="p-3 rounded-2xl bg-gradient-to-br from-lime-500 to-emerald-500 shadow-lg shadow-lime-500/20">
                                    <Bot className="w-8 h-8 text-white" />
                                </div>
                                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-400 rounded-full border-2 border-white dark:border-slate-900 animate-pulse" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-slate-800 dark:text-white">AI Agent Setup</h1>
                                <p className="text-slate-500 dark:text-gray-400 mt-1">Configure, manage, and monitor your intelligent automation agents</p>
                            </div>
                        </div>
                        <button onClick={handleCreateAgent} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-lime-500 to-emerald-500 text-white font-semibold text-sm hover:shadow-lg hover:shadow-lime-500/25 transition-all flex items-center gap-2">
                            <Plus className="w-4 h-4" /> Create Agent
                        </button>
                    </div>
                </motion.div>

                {/* Stats Cards */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    {[
                        { label: 'Total Agents', value: agents.length, icon: Users, gradient: 'from-blue-500 to-cyan-500' },
                        { label: 'Active Now', value: agents.filter(a => a.status === 'active').length, icon: Zap, gradient: 'from-emerald-500 to-teal-500' },
                        { label: 'Tasks Today', value: totalTasks, icon: Activity, gradient: 'from-violet-500 to-purple-500' },
                        { label: 'Success Rate', value: `${avgSuccessRate}%`, icon: TrendingUp, gradient: 'from-amber-500 to-orange-500' },
                    ].map((stat, idx) => (
                        <motion.div key={stat.label} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: idx * 0.05 }} className="relative overflow-hidden rounded-2xl border bg-white dark:bg-slate-800/50 border-slate-200 dark:border-white/5 backdrop-blur-sm shadow-sm">
                            <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-5`} />
                            <div className="relative p-5">
                                <div className="flex items-center justify-between mb-3">
                                    <div className={`p-2 rounded-xl bg-gradient-to-br ${stat.gradient}`}>
                                        <stat.icon className="w-5 h-5 text-white" />
                                    </div>
                                    <span className="text-xs text-slate-500 dark:text-gray-500 uppercase tracking-wider">{stat.label}</span>
                                </div>
                                <p className="text-3xl font-bold text-slate-800 dark:text-white">{stat.value}</p>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>

                {/* Agent Selector (for filtered views) */}
                {activeTab === 'mappings' && (
                    <div className="mb-6 p-4 rounded-xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-white/10">
                        <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Select Agent</label>
                        <div className="flex flex-wrap gap-2">
                            {agents.map(agent => (
                                <button
                                    key={agent.id}
                                    onClick={() => setSelectedAgent(agent)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${selectedAgent?.id === agent.id
                                        ? 'border-lime-500 bg-lime-50 dark:bg-lime-500/10 text-lime-700 dark:text-lime-400'
                                        : 'border-slate-200 dark:border-white/10 text-slate-600 dark:text-gray-400 hover:border-slate-300 dark:hover:border-white/20'
                                        }`}
                                >
                                    <img src={agent.customAvatar || agent.avatar} alt={agent.customName} className="w-6 h-6 rounded-full" />
                                    <span className="text-sm font-medium">{agent.customName}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Tabs */}
                <div className="flex items-center gap-2 mb-6 p-1 rounded-xl bg-slate-200/50 dark:bg-white/5 backdrop-blur-sm w-fit overflow-x-auto">
                    {[
                        { id: 'agents', label: 'AI Agents', icon: Bot },
                        { id: 'mappings', label: 'Document Mapping', icon: Link2 }
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm transition-all whitespace-nowrap ${activeTab === tab.id
                                ? 'bg-gradient-to-r from-lime-500 to-emerald-500 text-white shadow-lg shadow-lime-500/20'
                                : 'text-slate-600 dark:text-gray-400 hover:text-slate-800 dark:hover:text-white hover:bg-white/50 dark:hover:bg-white/5'
                                }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <AnimatePresence mode="wait">
                    {/* Agents Tab */}
                    {activeTab === 'agents' && (
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                            {agents.map((agent, idx) => (
                                <motion.div key={agent.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }} className="relative rounded-2xl border bg-white dark:bg-slate-800/50 border-slate-200 dark:border-white/10 overflow-hidden hover:border-slate-300 dark:hover:border-white/20 transition-all shadow-sm">
                                    <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${agent.gradient}`} />
                                    <div className="p-6">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex items-center gap-4">
                                                <div className="relative">
                                                    <img src={agent.customAvatar || agent.avatar} alt={agent.customName} className="w-16 h-16 rounded-2xl border-2 shadow-lg object-cover" style={{ borderColor: agent.color }} />
                                                    <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white dark:border-slate-800 flex items-center justify-center ${agent.status === 'active' ? 'bg-emerald-500' : 'bg-gray-400'}`}>
                                                        {agent.status === 'active' ? <Activity className="w-3 h-3 text-white" /> : <Pause className="w-3 h-3 text-white" />}
                                                    </div>
                                                </div>
                                                <div>
                                                    <h3 className="text-xl font-bold text-slate-800 dark:text-white">{agent.customName}</h3>
                                                    <p className="text-sm text-slate-500 dark:text-gray-400 flex items-center gap-1">
                                                        <agent.icon className="w-3 h-3" /> {agent.name}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <button onClick={() => handleEditAgent(agent)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 transition-colors" title="Edit Agent">
                                                    <Edit3 className="w-4 h-4 text-slate-400 hover:text-slate-600 dark:hover:text-white" />
                                                </button>
                                                <button onClick={() => handleDeleteAgent(agent.id)} className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/20 transition-colors" title="Delete Agent">
                                                    <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" />
                                                </button>
                                            </div>
                                        </div>
                                        <p className="text-sm text-slate-500 dark:text-gray-400 mb-4 line-clamp-2">{agent.description}</p>
                                        {agent.emailConfig?.emailAddress && (
                                            <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-slate-50 dark:bg-white/5">
                                                <Mail className="w-4 h-4 text-slate-400" />
                                                <span className="text-xs text-slate-600 dark:text-gray-400 font-mono">{agent.emailConfig.emailAddress}</span>
                                            </div>
                                        )}
                                        <div className="mb-4">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Sparkles className="w-4 h-4 text-lime-500" />
                                                <span className="text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Capabilities</span>
                                            </div>
                                            <div className="flex flex-wrap gap-1">
                                                {agent.capabilities.slice(0, 3).map((cap, i) => (
                                                    <span key={i} className="px-2 py-1 text-xs rounded-md bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-gray-400">{cap}</span>
                                                ))}
                                                {agent.capabilities.length > 3 && (
                                                    <span className="px-2 py-1 text-xs rounded-md bg-lime-100 dark:bg-lime-500/20 text-lime-700 dark:text-lime-400">+{agent.capabilities.length - 3} more</span>
                                                )}
                                            </div>
                                        </div>
                                        {/* PDF Mapping Badge */}
                                        {agent.requiresPdfMapping && (
                                            <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-500/10">
                                                <Link2 className="w-4 h-4 text-blue-500" />
                                                <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">Document Mapping Enabled</span>
                                                <span className="text-xs text-blue-500">({agent.pdfMappings?.length || 0} templates)</span>
                                            </div>
                                        )}
                                        <div className="grid grid-cols-3 gap-3 p-4 rounded-xl bg-slate-50 dark:bg-white/5 mb-4">
                                            <div className="text-center">
                                                <p className="text-lg font-bold text-slate-800 dark:text-white">{agent.tasksCompleted}</p>
                                                <p className="text-xs text-slate-400">Tasks</p>
                                            </div>
                                            <div className="text-center border-x border-slate-200 dark:border-white/10">
                                                <p className="text-lg font-bold text-emerald-500">{agent.successRate}%</p>
                                                <p className="text-xs text-slate-400">Success</p>
                                            </div>
                                            <div className="text-center">
                                                <p className="text-lg font-bold text-slate-800 dark:text-white">{agent.totalHours}h</p>
                                                <p className="text-xs text-slate-400">Runtime</p>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button onClick={() => handleEditAgent(agent)} className="flex-1 px-4 py-2.5 rounded-lg bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-white text-sm font-medium hover:bg-slate-200 dark:hover:bg-white/10 transition-colors flex items-center justify-center gap-2">
                                                <Settings className="w-4 h-4" /> Configure
                                            </button>
                                            <button onClick={() => handleStatusToggle(agent.id)} className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${agent.status === 'active' ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400 hover:bg-amber-200' : 'bg-gradient-to-r from-lime-500 to-emerald-500 text-white hover:shadow-lg hover:shadow-lime-500/25'}`}>
                                                {agent.status === 'active' ? <><Pause className="w-4 h-4" /> Pause</> : <><Play className="w-4 h-4" /> Start</>}
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>
                    )}

                    {/* Email Inbox Tab */}
                    {activeTab === 'inbox' && (
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                            <div className="flex flex-col sm:flex-row gap-3 mb-6">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <input type="text" placeholder="Search emails..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className={`${inputClass} pl-10`} />
                                </div>
                                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={`${selectClass} w-full sm:w-40`}>
                                    <option value="all">All Status</option>
                                    <option value="pending">Pending</option>
                                    <option value="completed">Completed</option>
                                    <option value="hold">On Hold</option>
                                    <option value="exception">Exception</option>
                                </select>
                            </div>
                            {selectedAgent && (
                                <div className="mb-4 p-3 rounded-lg bg-slate-50 dark:bg-white/5 flex items-center gap-2">
                                    <img src={selectedAgent.customAvatar || selectedAgent.avatar} alt={selectedAgent.customName} className="w-8 h-8 rounded-full" />
                                    <span className="font-medium text-slate-700 dark:text-white">{selectedAgent.customName}'s Inbox</span>
                                    <span className="text-sm text-slate-500">({filteredEmails.length} emails)</span>
                                </div>
                            )}
                            <div className="space-y-3">
                                {filteredEmails.length === 0 ? (
                                    <div className="text-center py-12">
                                        <Inbox className="w-12 h-12 text-slate-300 dark:text-gray-600 mx-auto mb-3" />
                                        <p className="text-slate-500 dark:text-gray-400">No emails found for this agent</p>
                                    </div>
                                ) : (
                                    filteredEmails.map((email) => (
                                        <div key={email.id} className="p-4 rounded-xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20 transition-all">
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex items-start gap-4 flex-1 min-w-0">
                                                    {getStatusIcon(email.status)}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <h4 className="text-sm font-semibold text-slate-700 dark:text-white truncate">{email.subject}</h4>
                                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(email.status)}`}>{email.status}</span>
                                                        </div>
                                                        <p className="text-xs text-slate-500 dark:text-gray-400 mb-1">From: {email.sender}</p>
                                                        <p className="text-xs text-slate-400 dark:text-gray-500 truncate">{email.preview}</p>
                                                    </div>
                                                </div>
                                                <span className="text-xs text-slate-400 flex-shrink-0">{email.date}</span>
                                            </div>
                                            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100 dark:border-white/5">
                                                <button onClick={() => handleEmailStatus(email.id, 'completed')} className="px-3 py-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 text-xs font-medium hover:bg-emerald-200 dark:hover:bg-emerald-500/30 transition-colors flex items-center gap-1">
                                                    <CheckCircle className="w-3 h-3" /> Completed
                                                </button>
                                                <button onClick={() => handleEmailStatus(email.id, 'hold')} className="px-3 py-1.5 rounded-lg bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 text-xs font-medium hover:bg-amber-200 dark:hover:bg-amber-500/30 transition-colors flex items-center gap-1">
                                                    <PauseCircle className="w-3 h-3" /> On Hold
                                                </button>
                                                <button onClick={() => handleEmailStatus(email.id, 'exception')} className="px-3 py-1.5 rounded-lg bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 text-xs font-medium hover:bg-red-200 dark:hover:bg-red-500/30 transition-colors flex items-center gap-1">
                                                    <AlertCircle className="w-3 h-3" /> Exception
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </motion.div>
                    )}

                    {/* Dashboard Tab */}
                    {activeTab === 'dashboard' && selectedAgent && (
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    <img src={selectedAgent.customAvatar || selectedAgent.avatar} alt={selectedAgent.customName} className="w-10 h-10 rounded-full" />
                                    <h2 className="text-xl font-bold text-slate-800 dark:text-white">{selectedAgent.customName}'s Dashboard</h2>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-slate-400" />
                                    <select value={dateRange} onChange={(e) => setDateRange(e.target.value)} className={`${selectClass} w-40`}>
                                        <option value="7d">Last 7 Days</option>
                                        <option value="30d">Last 30 Days</option>
                                        <option value="90d">Last 90 Days</option>
                                        <option value="ytd">Year to Date</option>
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                                <div className="p-6 rounded-2xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-white/10">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-sm font-semibold text-slate-700 dark:text-white">Tasks Processed by Date</h3>
                                        <LineChart className="w-5 h-5 text-slate-400" />
                                    </div>
                                    <div className="h-48 flex items-end justify-between gap-2">
                                        {(selectedAgent.dashboardData?.tasksByDate || []).map((day, idx) => (
                                            <div key={idx} className="flex-1 flex flex-col items-center">
                                                <div className="w-full bg-gradient-to-t from-lime-500 to-emerald-500 rounded-t-lg transition-all hover:from-lime-400 hover:to-emerald-400" style={{ height: `${(day.tasks / 60) * 100}%` }} />
                                                <span className="text-xs text-slate-400 mt-2">{day.date}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="p-6 rounded-2xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-white/10">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-sm font-semibold text-slate-700 dark:text-white">Status Breakdown</h3>
                                        <PieChart className="w-5 h-5 text-slate-400" />
                                    </div>
                                    <div className="flex items-center gap-6">
                                        <div className="relative w-32 h-32">
                                            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                                                <circle cx="18" cy="18" r="16" fill="none" stroke="#e2e8f0" strokeWidth="3" className="dark:stroke-slate-700" />
                                                <circle cx="18" cy="18" r="16" fill="none" stroke="#10b981" strokeWidth="3" strokeDasharray={`${((selectedAgent.dashboardData?.statusBreakdown?.completed || 0) / ((selectedAgent.dashboardData?.statusBreakdown?.completed || 0) + (selectedAgent.dashboardData?.statusBreakdown?.hold || 0) + (selectedAgent.dashboardData?.statusBreakdown?.exception || 0))) * 100} 100`} />
                                            </svg>
                                        </div>
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                                                <span className="text-sm text-slate-600 dark:text-gray-400">Completed</span>
                                                <span className="text-sm font-bold text-slate-800 dark:text-white">{selectedAgent.dashboardData?.statusBreakdown?.completed || 0}</span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="w-3 h-3 rounded-full bg-amber-500" />
                                                <span className="text-sm text-slate-600 dark:text-gray-400">On Hold</span>
                                                <span className="text-sm font-bold text-slate-800 dark:text-white">{selectedAgent.dashboardData?.statusBreakdown?.hold || 0}</span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="w-3 h-3 rounded-full bg-red-500" />
                                                <span className="text-sm text-slate-600 dark:text-gray-400">Exception</span>
                                                <span className="text-sm font-bold text-slate-800 dark:text-white">{selectedAgent.dashboardData?.statusBreakdown?.exception || 0}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {/* PDF Mappings Tab */}
                    {activeTab === 'mappings' && (
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                            {selectedAgent?.requiresPdfMapping ? (
                                <>
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="flex items-center gap-3">
                                            <img src={selectedAgent.customAvatar || selectedAgent.avatar} alt={selectedAgent.customName} className="w-10 h-10 rounded-full" />
                                            <div>
                                                <h2 className="text-xl font-bold text-slate-800 dark:text-white">{selectedAgent.customName}'s Document Mappings</h2>
                                                <p className="text-slate-500 dark:text-gray-400 text-sm">Configure how carrier documents are parsed and mapped to API fields</p>
                                            </div>
                                        </div>
                                        <button onClick={handleAddMapping} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-lime-500 to-emerald-500 text-white font-semibold text-sm hover:shadow-lg hover:shadow-lime-500/25 transition-all flex items-center gap-2">
                                            <Plus className="w-4 h-4" /> Add Mapping
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {(selectedAgent.pdfMappings || []).map((mapping) => (
                                            <div key={mapping.id} className="p-5 rounded-xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20 transition-all">
                                                <div className="flex items-start justify-between mb-4">
                                                    <div className="flex items-center gap-3">
                                                        {/* Carrier Logo Upload */}
                                                        <div className="relative group">
                                                            <input
                                                                type="file"
                                                                id={`carrier-logo-${mapping.id}`}
                                                                accept="image/jpeg,image/jpg,image/png,image/svg+xml,image/webp"
                                                                onChange={(e) => handleCarrierLogoUpload(e, mapping.id)}
                                                                className="hidden"
                                                            />
                                                            <label
                                                                htmlFor={`carrier-logo-${mapping.id}`}
                                                                className="cursor-pointer block"
                                                            >
                                                                {mapping.carrierLogo ? (
                                                                    <div className="w-12 h-12 rounded-lg border-2 border-slate-200 dark:border-white/20 overflow-hidden hover:border-blue-500 dark:hover:border-blue-400 transition-colors relative">
                                                                        <img
                                                                            src={mapping.carrierLogo}
                                                                            alt={mapping.carrier || 'Carrier'}
                                                                            className="w-full h-full object-contain bg-white"
                                                                        />
                                                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                                            <Upload className="w-4 h-4 text-white" />
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-500/20 border-2 border-dashed border-blue-300 dark:border-blue-500/40 hover:border-blue-500 dark:hover:border-blue-400 transition-colors flex flex-col items-center justify-center group-hover:bg-blue-200 dark:group-hover:bg-blue-500/30">
                                                                        <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400 group-hover:hidden" />
                                                                        <Upload className="w-4 h-4 text-blue-600 dark:text-blue-400 hidden group-hover:block" />
                                                                        <span className="text-[8px] text-blue-500 dark:text-blue-400 mt-0.5 hidden group-hover:block">Logo</span>
                                                                    </div>
                                                                )}
                                                            </label>
                                                        </div>
                                                        <div>
                                                            <h4 className="font-semibold text-slate-700 dark:text-white">{mapping.carrier || 'New Mapping'}</h4>
                                                            <span className={`text-xs px-2 py-0.5 rounded-full ${mapping.status === 'active' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400'}`}>
                                                                {mapping.status}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <button onClick={() => handleEditMapping(mapping)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 transition-colors">
                                                        <Edit3 className="w-4 h-4 text-slate-400" />
                                                    </button>
                                                </div>
                                                <div className="space-y-2 text-sm">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-slate-500 dark:text-gray-400">Fields Mapped</span>
                                                        <span className="font-medium text-slate-700 dark:text-white">{mapping.fields?.length || 0}</span>
                                                    </div>
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-slate-500 dark:text-gray-400">Last Updated</span>
                                                        <span className="font-medium text-slate-700 dark:text-white">{mapping.lastUpdated}</span>
                                                    </div>
                                                </div>
                                                <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100 dark:border-white/5">
                                                    <button onClick={() => handlePreviewMapping(mapping)} className="flex-1 px-3 py-2 rounded-lg bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-gray-400 text-xs font-medium hover:bg-slate-200 dark:hover:bg-white/10 transition-colors flex items-center justify-center gap-1">
                                                        <Eye className="w-3 h-3" /> Preview
                                                    </button>
                                                    <button onClick={() => handleEditMapping(mapping)} className="flex-1 px-3 py-2 rounded-lg bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 text-xs font-medium hover:bg-blue-200 dark:hover:bg-blue-500/30 transition-colors flex items-center justify-center gap-1">
                                                        <Link2 className="w-3 h-3" /> Map Fields
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <div className="text-center py-16">
                                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 dark:bg-white/5 mb-4">
                                        <Link2 className="w-8 h-8 text-slate-400" />
                                    </div>
                                    <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">Document Mapping Not Required</h3>
                                    <p className="text-slate-500 dark:text-gray-400 max-w-md mx-auto">
                                        {selectedAgent?.customName} ({selectedAgent?.name}) does not require PDF mapping configuration.
                                        PDF mapping is only available for Booking Agent, B/L Agent, and Accounting Agent.
                                    </p>
                                </div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Create/Edit Agent Modal */}
            <AnimatePresence>
                {(showAgentModal || showCreateModal) && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => { setShowAgentModal(false); setShowCreateModal(false); }}>
                        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-white/10 w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-xl" onClick={(e) => e.stopPropagation()}>
                            <div className="p-6 border-b border-slate-100 dark:border-white/10 bg-gradient-to-r from-lime-500/10 to-emerald-500/10">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-xl bg-gradient-to-br from-lime-500 to-emerald-500">
                                            {editingAgent ? <Edit3 className="w-5 h-5 text-white" /> : <Plus className="w-5 h-5 text-white" />}
                                        </div>
                                        <h2 className="text-xl font-bold text-slate-800 dark:text-white">{editingAgent ? `Edit ${editingAgent.customName}` : 'Create New Agent'}</h2>
                                    </div>
                                    <button onClick={() => { setShowAgentModal(false); setShowCreateModal(false); }} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 transition-colors">
                                        <X className="w-5 h-5 text-slate-400" />
                                    </button>
                                </div>
                            </div>
                            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)] space-y-5">
                                {/* Agent Name */}
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Agent Name <span className="text-red-500">*</span></label>
                                    <input type="text" value={agentForm.customName} onChange={(e) => setAgentForm(prev => ({ ...prev, customName: e.target.value }))} placeholder="e.g., Rate Quote Agent" className={inputClass} />
                                </div>

                                {/* Avatar Selection with Upload */}
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Agent Avatar / Profile Picture</label>
                                    <div className="flex flex-wrap gap-3">
                                        {agentForm.customAvatar && (
                                            <button type="button" onClick={() => { }} className="relative rounded-xl p-1 ring-2 ring-lime-500 ring-offset-2 dark:ring-offset-slate-800">
                                                <img src={agentForm.customAvatar} alt="Custom" className="w-12 h-12 rounded-lg object-cover" />
                                                <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-lime-500 flex items-center justify-center">
                                                    <Check className="w-3 h-3 text-white" />
                                                </div>
                                            </button>
                                        )}
                                        {defaultAvatarOptions.map((avatar, idx) => (
                                            <button key={idx} type="button" onClick={() => setAgentForm(prev => ({ ...prev, avatar, customAvatar: null }))} className={`relative rounded-xl p-1 transition-all ${!agentForm.customAvatar && agentForm.avatar === avatar ? 'ring-2 ring-lime-500 ring-offset-2 dark:ring-offset-slate-800' : 'hover:ring-2 hover:ring-slate-200 dark:hover:ring-white/20'}`}>
                                                <img src={avatar} alt={`Avatar ${idx + 1}`} className="w-12 h-12 rounded-lg" />
                                                {!agentForm.customAvatar && agentForm.avatar === avatar && (
                                                    <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-lime-500 flex items-center justify-center">
                                                        <Check className="w-3 h-3 text-white" />
                                                    </div>
                                                )}
                                            </button>
                                        ))}

                                        <button type="button" onClick={() => fileInputRef.current?.click()} className="w-14 h-14 rounded-xl border-2 border-dashed border-slate-200 dark:border-white/10 flex flex-col items-center justify-center hover:border-lime-500 dark:hover:border-lime-500 transition-colors">
                                            <Upload className="w-5 h-5 text-slate-400" />
                                            <span className="text-[10px] text-slate-400 mt-1">Upload</span>
                                        </button>
                                        <input ref={fileInputRef} type="file" accept="image/jpeg,image/jpg,image/png" onChange={handleImageUpload} className="hidden" />
                                    </div>
                                    <p className="text-xs text-slate-400 mt-2">JPG or PNG, max 2MB. Click Upload to add custom image.</p>
                                </div>

                                {/* Description */}
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Business Details / Description</label>
                                    <textarea value={agentForm.description} onChange={(e) => setAgentForm(prev => ({ ...prev, description: e.target.value }))} placeholder="Describe what this agent handles..." rows={3} className={`${inputClass} resize-none`} />
                                </div>

                                {/* System Prompt */}
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">System Prompt / Instructions <span className="text-red-500">*</span></label>
                                    <textarea value={agentForm.systemPrompt} onChange={(e) => setAgentForm(prev => ({ ...prev, systemPrompt: e.target.value }))} placeholder="Define the agent's role, tone, guidelines..." rows={6} className={`${inputClass} resize-none font-mono text-sm`} />
                                </div>

                                {/* Email Configuration Section */}
                                <div className="border border-slate-200 dark:border-white/10 rounded-xl p-4">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Mail className="w-5 h-5 text-lime-500" />
                                        <h3 className="text-lg font-semibold text-slate-700 dark:text-white">Email Configuration</h3>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-medium text-slate-500 dark:text-gray-400 mb-1">Email Provider Type</label>
                                            <select value={agentForm.emailConfig.provider} onChange={(e) => handleEmailConfigChange('provider', e.target.value)} className={selectClass}>
                                                <option value="imap">IMAP/SMTP</option>
                                                <option value="gmail">Gmail API</option>
                                                <option value="outlook">Outlook/Office 365</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-slate-500 dark:text-gray-400 mb-1">Email Address</label>
                                            <input type="email" value={agentForm.emailConfig.emailAddress} onChange={(e) => handleEmailConfigChange('emailAddress', e.target.value)} placeholder="agent@company.com" className={inputClass} />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-slate-500 dark:text-gray-400 mb-1">IMAP Host</label>
                                            <input type="text" value={agentForm.emailConfig.imapHost} onChange={(e) => handleEmailConfigChange('imapHost', e.target.value)} placeholder="imap.company.com" className={inputClass} />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-slate-500 dark:text-gray-400 mb-1">IMAP Port</label>
                                            <input type="number" value={agentForm.emailConfig.imapPort} onChange={(e) => handleEmailConfigChange('imapPort', parseInt(e.target.value))} className={inputClass} />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-slate-500 dark:text-gray-400 mb-1">SMTP Host</label>
                                            <input type="text" value={agentForm.emailConfig.smtpHost} onChange={(e) => handleEmailConfigChange('smtpHost', e.target.value)} placeholder="smtp.company.com" className={inputClass} />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-slate-500 dark:text-gray-400 mb-1">SMTP Port</label>
                                            <input type="number" value={agentForm.emailConfig.smtpPort} onChange={(e) => handleEmailConfigChange('smtpPort', parseInt(e.target.value))} className={inputClass} />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-slate-500 dark:text-gray-400 mb-1">Email Password / App Password</label>
                                            <input type="password" value={agentForm.emailConfig.password} onChange={(e) => handleEmailConfigChange('password', e.target.value)} placeholder="••••••••" className={inputClass} />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-slate-500 dark:text-gray-400 mb-1">Inbox Folder Name</label>
                                            <input type="text" value={agentForm.emailConfig.inboxFolder} onChange={(e) => handleEmailConfigChange('inboxFolder', e.target.value)} placeholder="INBOX" className={inputClass} />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-slate-500 dark:text-gray-400 mb-1">Polling Interval (seconds)</label>
                                            <input type="number" value={agentForm.emailConfig.pollingInterval} onChange={(e) => handleEmailConfigChange('pollingInterval', parseInt(e.target.value))} className={inputClass} />
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input type="checkbox" checked={agentForm.emailConfig.useSSL} onChange={(e) => handleEmailConfigChange('useSSL', e.target.checked)} className="rounded border-slate-300 text-lime-500 focus:ring-lime-500" />
                                                <span className="text-sm text-slate-600 dark:text-gray-400">Use SSL/TLS</span>
                                            </label>
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input type="checkbox" checked={agentForm.emailConfig.autoReply} onChange={(e) => handleEmailConfigChange('autoReply', e.target.checked)} className="rounded border-slate-300 text-lime-500 focus:ring-lime-500" />
                                                <span className="text-sm text-slate-600 dark:text-gray-400">Auto Reply</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                {/* Tools Selection */}
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Tools & Capabilities</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {availableTools.map((tool) => (
                                            <button key={tool.id} type="button" onClick={() => toggleTool(tool.id)} className={`p-3 rounded-xl border text-left transition-all ${agentForm.tools.includes(tool.id) ? 'border-lime-500 bg-lime-50 dark:bg-lime-500/10' : 'border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20'}`}>
                                                <div className="flex items-center gap-2 mb-1">
                                                    {agentForm.tools.includes(tool.id) ? <Check className="w-4 h-4 text-lime-600 dark:text-lime-400" /> : <div className="w-4 h-4 rounded border border-slate-300 dark:border-white/20" />}
                                                    <span className={`text-sm font-medium ${agentForm.tools.includes(tool.id) ? 'text-lime-700 dark:text-lime-400' : 'text-slate-700 dark:text-white'}`}>{tool.name}</span>
                                                </div>
                                                <p className="text-xs text-slate-500 dark:text-gray-400 ml-6">{tool.description}</p>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Status */}
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Status</label>
                                    <div className="flex gap-3">
                                        <button type="button" onClick={() => setAgentForm(prev => ({ ...prev, status: 'active' }))} className={`flex-1 px-4 py-3 rounded-xl border transition-all flex items-center justify-center gap-2 ${agentForm.status === 'active' ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400' : 'border-slate-200 dark:border-white/10 text-slate-600 dark:text-gray-400 hover:border-slate-300'}`}>
                                            <Play className="w-4 h-4" /> Active
                                        </button>
                                        <button type="button" onClick={() => setAgentForm(prev => ({ ...prev, status: 'inactive' }))} className={`flex-1 px-4 py-3 rounded-xl border transition-all flex items-center justify-center gap-2 ${agentForm.status === 'inactive' ? 'border-amber-500 bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400' : 'border-slate-200 dark:border-white/10 text-slate-600 dark:text-gray-400 hover:border-slate-300'}`}>
                                            <Pause className="w-4 h-4" /> Inactive
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="p-6 border-t border-slate-100 dark:border-white/10 flex gap-3">
                                <button onClick={() => { setShowAgentModal(false); setShowCreateModal(false); }} className="flex-1 px-4 py-3 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-white font-medium hover:bg-slate-200 dark:hover:bg-white/10 transition-colors" disabled={isSaving}>Cancel</button>
                                <button onClick={handleSaveAgent} disabled={isSaving} className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-lime-500 to-emerald-500 text-white font-semibold hover:shadow-lg hover:shadow-lime-500/25 transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                                    {isSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {isSaving ? 'Saving...' : (editingAgent ? 'Save Changes' : 'Create Agent')}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* PDF Mapping Editor Modal */}
            <AnimatePresence>
                {showMappingEditor && editingMapping && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => { setShowMappingEditor(false); setEditingMapping(null); setTestResults(null); }}>
                        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-white/10 w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-xl" onClick={(e) => e.stopPropagation()}>
                            <div className="p-6 border-b border-slate-100 dark:border-white/10 bg-gradient-to-r from-blue-500/10 to-cyan-500/10">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500">
                                            <Link2 className="w-5 h-5 text-white" />
                                        </div>
                                        <h2 className="text-xl font-bold text-slate-800 dark:text-white">PDF Field Mapping Editor</h2>
                                    </div>
                                    <button onClick={() => { setShowMappingEditor(false); setEditingMapping(null); setTestResults(null); }} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 transition-colors">
                                        <X className="w-5 h-5 text-slate-400" />
                                    </button>
                                </div>
                            </div>
                            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                                {/* Carrier Name */}
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Carrier / Template Name</label>
                                    <input type="text" value={editingMapping.carrier} onChange={(e) => setEditingMapping(prev => ({ ...prev, carrier: e.target.value }))} placeholder="e.g., Maersk, MSC, CMA CGM" className={inputClass} />
                                </div>

                                {/* PDF Upload */}
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Upload Sample PDF</label>
                                    {editingMapping.samplePdf ? (
                                        <div className="space-y-3">
                                            {/* PDF Preview */}
                                            <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden bg-slate-50 dark:bg-slate-800">
                                                <embed src={editingMapping.samplePdf} type="application/pdf" className="w-full h-[400px]" />
                                            </div>
                                            {/* File info & replace */}
                                            <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-2.5">
                                                <div className="flex items-center gap-3">
                                                    <FileText className="w-5 h-5 text-blue-500" />
                                                    <div>
                                                        <p className="text-sm font-medium text-slate-800 dark:text-white">{editingMapping.pdfFileName || 'sample.pdf'}</p>
                                                        <p className="text-[10px] text-slate-500">Document uploaded successfully</p>
                                                    </div>
                                                </div>
                                                <button onClick={() => pdfInputRef.current?.click()} className="px-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                                                    Replace
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div onClick={() => pdfInputRef.current?.click()} className="border-2 border-dashed border-slate-200 dark:border-white/10 rounded-xl p-8 text-center hover:border-blue-500 dark:hover:border-blue-500 transition-colors cursor-pointer">
                                            <FileUp className="w-12 h-12 text-slate-300 dark:text-gray-600 mx-auto mb-4" />
                                            <p className="text-slate-600 dark:text-gray-400 mb-2">Drag and drop a PDF, or click to browse</p>
                                            <p className="text-xs text-slate-400">Upload a sample booking confirmation from this carrier</p>
                                        </div>
                                    )}
                                    <input ref={pdfInputRef} type="file" accept="application/pdf" onChange={handlePdfUpload} className="hidden" />
                                </div>

                                {/* Field Mappings */}
                                <div className="mb-6">
                                    <div className="flex items-center justify-between mb-3">
                                        <label className="text-sm font-medium text-slate-700 dark:text-gray-300">Field Mappings</label>
                                        <button onClick={handleAddField} className="px-3 py-1.5 rounded-lg bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 text-xs font-medium hover:bg-blue-200 dark:hover:bg-blue-500/30 transition-colors flex items-center gap-1">
                                            <Plus className="w-3 h-3" /> Add Field
                                        </button>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="grid grid-cols-12 gap-3 text-xs font-medium text-slate-500 dark:text-gray-400 px-2">
                                            <div className="col-span-4">PDF Source Field</div>
                                            <div className="col-span-1 text-center"></div>
                                            <div className="col-span-4">API Target Field</div>
                                            <div className="col-span-2">Test Value</div>
                                            <div className="col-span-1"></div>
                                        </div>
                                        {editingMapping.fields.map((field, index) => (
                                            <div key={index} className="grid grid-cols-12 gap-3 items-center">
                                                <div className="col-span-4">
                                                    <input type="text" value={field.source} onChange={(e) => handleFieldChange(index, 'source', e.target.value)} placeholder="PDF field name" className={inputClass} />
                                                </div>
                                                <div className="col-span-1 flex justify-center">
                                                    <ArrowRight className="w-4 h-4 text-slate-400" />
                                                </div>
                                                <div className="col-span-4">
                                                    <select value={field.target} onChange={(e) => handleFieldChange(index, 'target', e.target.value)} className={selectClass}>
                                                        <option value="">Select API field...</option>
                                                        {apiFieldOptions.map(opt => (
                                                            <option key={opt} value={opt}>{opt}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-span-2">
                                                    <input type="text" value={field.value} onChange={(e) => handleFieldChange(index, 'value', e.target.value)} placeholder="Test value" className={inputClass} />
                                                </div>
                                                <div className="col-span-1">
                                                    <button onClick={() => handleRemoveField(index)} className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/20 text-slate-400 hover:text-red-500 transition-colors">
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                        {editingMapping.fields.length === 0 && (
                                            <div className="text-center py-8 text-slate-400">
                                                <Layers className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                                <p className="text-sm">No fields mapped yet. Click "Add Field" to start.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Test Results */}
                                {testResults && (
                                    <div className="mb-6 p-4 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10">
                                        <div className="flex items-center gap-2 mb-3">
                                            <TestTube className="w-5 h-5 text-emerald-500" />
                                            <h4 className="font-semibold text-slate-700 dark:text-white">Test Extraction Results</h4>
                                            {testResults.processingTime && (
                                                <span className="text-xs text-slate-500">({testResults.processingTime})</span>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                            {testResults.extractedFields.map((field, idx) => (
                                                <div key={idx} className="p-3 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-white/10">
                                                    <p className="text-xs text-slate-500 dark:text-gray-400 mb-1">{field.field}</p>
                                                    <p className="text-sm font-medium text-slate-700 dark:text-white">{field.value}</p>
                                                    <div className="flex items-center gap-1 mt-1">
                                                        <div className={`h-1 rounded-full flex-1 ${field.confidence > 90 ? 'bg-emerald-500' : field.confidence > 70 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${field.confidence}%` }} />
                                                        <span className="text-xs text-slate-400">{field.confidence}%</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="p-6 border-t border-slate-100 dark:border-white/10 flex gap-3">
                                <button onClick={() => { setShowMappingEditor(false); setEditingMapping(null); setTestResults(null); }} className="px-4 py-3 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-white font-medium hover:bg-slate-200 dark:hover:bg-white/10 transition-colors" disabled={isSaving}>Cancel</button>
                                <button onClick={handleTestMapping} disabled={isTestingMapping || isSaving} className="px-4 py-3 rounded-xl bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 font-medium hover:bg-amber-200 dark:hover:bg-amber-500/30 transition-colors flex items-center gap-2 disabled:opacity-50">
                                    {isTestingMapping ? <RefreshCw className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
                                    {isTestingMapping ? 'Testing...' : 'Test Extraction'}
                                </button>
                                <button onClick={handleSaveMapping} disabled={isSaving} className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold hover:shadow-lg hover:shadow-blue-500/25 transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                                    {isSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {isSaving ? 'Saving...' : 'Save Mapping'}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AIAgentSetup;
